import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { apiRoot } from "../constants";

// This is the main hook of the app, which uses context.
// It uses context because several of the exports below are to be shared
// between other hooks and views.

const authContext = createContext();

export function AuthProvider({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export const useAuth = () => {
  return useContext(authContext);
};

function useProvideAuth() {
  const t = localStorage.getItem("token");

  const [authed, setAuthed] = useState(!!t);
  const [token, setToken] = useState(t);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  const fetchUser = useCallback((token) => {
    if (!token) {
      return;
    }
    fetch(`${apiRoot}/auth/user/`, {
      headers: {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((resp) => {
        if (resp.status === 200) {
          resp.json().then((json) => {
            setProfile(json);
            setError(false);
          });
        } else {
          setError(true);
          setProfile(null);
          setAuthed(false);
          localStorage.removeItem("token");
        }
      })
      .catch((err) => {
        setError(true);
        setProfile(null);
        setAuthed(false);
        localStorage.removeItem("token");
      });
  }, []);

  useEffect(() => {
    fetchUser(t);
  }, [fetchUser, t]);

  if (!!t && !profile) {
    fetchUser(t);
  }
  return {
    token,
    error,
    errors,
    authed,
    profile,
    login(email, password) {
      setErrors({});
      setError(false);
      return new Promise((res) => {
        const data = {
          email: email,
          password: password,
        };
        fetch(`${apiRoot}/auth/login/`, {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((resp) => {
            if (resp.status === 200) {
              resp.json().then((json) => {
                if (json.key) {
                  setAuthed(true);
                  localStorage.setItem("token", json.key);
                  setToken(json.key);
                  // fetch user will happen now because token is a dependency of a useEffect
                  res(true);
                } else {
                  res(false);
                  setError(true);
                }
              });
            } else {
              const contentType = resp.headers.get("content-type");
              if (contentType.includes("json")) {
                resp.json().then((json) => {
                  setErrors(json);
                });
              }
              setError(true);
              res(false);
            }
          })
          .catch((e) => {
            setError(true);
          });
      });
    },
    updateUser(fields) {
      return new Promise((res) => {
        const data = {};

        const updatableFields = [
          { stateName: "name", backendFieldName: "name" },
          { stateName: "bio", backendFieldName: "bio" },
          { stateName: "favoriteFood", backendFieldName: "favorite_food" },
          {
            stateName: "newsletterSubscribed",
            backendFieldName: "newsletter_subscribed",
          },
        ];

        updatableFields.forEach((field) => {
          if (profile[field.backendFieldName] !== fields[field.stateName]) {
            data[field.backendFieldName] = fields[field.stateName];
          }
        });

        fetch(`${apiRoot}/auth/user/`, {
          method: "PUT",
          body: JSON.stringify(data),
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        })
          .then((resp) => {
            if (resp.status === 200) {
              resp.json().then((json) => {
                setProfile(json);
                res(true);
              });
            } else {
              resp.json().then(() => {
                res(false);
                setError(true);
              });
            }
          })
          .catch((e) => {
            setError(true);
          });
      });
    },
    changePassword(password, passwordConfirm) {
      return new Promise((res) => {
        const data = {
          new_password1: password,
          new_password2: passwordConfirm,
        };
        fetch(`${apiRoot}/auth/password/change/`, {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        })
          .then((resp) => {
            if (resp.status === 200) {
              resp.json().then((json) => {
                res(true);
              });
            } else {
              resp.json().then(() => {
                res(false);
                setError(true);
              });
            }
          })
          .catch((e) => {
            setError(true);
          });
      });
    },
    requestPasswordReset(email) {
      return new Promise((res) => {
        const data = {
          email: email,
        };
        fetch(`${apiRoot}/auth/password/reset/`, {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((resp) => {
            if (resp.status === 200) {
              resp.json().then((json) => {
                res(true);
              });
            } else {
              resp.json().then(() => {
                res(false);
                setError(true);
              });
            }
          })
          .catch((e) => {
            setError(true);
          });
      });
    },
    requestEmailConfirm(email) {
      return new Promise((res) => {
        const data = {
          email: email,
        };
        fetch(`${apiRoot}/auth/registration/resend-email/`, {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((resp) => {
            if (resp.status === 200) {
              resp.json().then((json) => {
                res(true);
              });
            } else {
              resp.json().then(() => {
                res(false);
                setError(true);
              });
            }
          })
          .catch((e) => {
            setError(true);
          });
      });
    },
    resetPassword(password, passwordConfirm, id, token) {
      return new Promise((res) => {
        const data = {
          new_password1: password,
          new_password2: passwordConfirm,
          uid: id,
          token: token,
        };
        fetch(`${apiRoot}/auth/password/reset/confirm/`, {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((resp) => {
            if (resp.status === 200) {
              resp.json().then((json) => {
                res(true);
              });
            } else {
              resp.json().then(() => {
                res(false);
                setError(true);
              });
            }
          })
          .catch((e) => {
            setError(true);
          });
      });
    },
    confirmEmail(token) {
      return new Promise((res) => {
        const data = {
          key: token,
        };
        fetch(`${apiRoot}/auth/registration/account-confirm-email/`, {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((resp) => {
            if (resp.status === 200) {
              resp.json().then((json) => {
                res(true);
              });
            } else {
              resp.json().then(() => {
                res(false);
                setError(true);
              });
            }
          })
          .catch((e) => {
            setError(true);
          });
      });
    },
    register(email, password, passwordConfirm, username, newsletterSubscribed) {
      setErrors({});
      setError(false);
      return new Promise((res) => {
        const data = {
          username: username,
          email: email,
          password1: password,
          password2: passwordConfirm,
          newsletter_subscribed: newsletterSubscribed,
        };
        fetch(`${apiRoot}/auth/registration/register/`, {
          method: "POST",
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((resp) => {
            if (resp.status === 201) {
              resp.json().then((json) => {
                if (json.detail === "Verification e-mail sent.") {
                  res(true);
                } else {
                  res(false);
                  setError(true);
                }
              });
            } else {
              resp.json().then((json) => {
                setError(true);
                setErrors(json);
                res(false);
              });
            }
          })
          .catch((e) => {
            setError(true);
          });
      });
    },
    logout() {
      return new Promise((res) => {
        setAuthed(false);
        setProfile(null);
        localStorage.removeItem("token");
        res();
      });
    },
  };
}
